// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import Widget from '@arcgis/core/widgets/Widget';
import { tsx } from '@arcgis/core/widgets/support/widget';
import SceneView from '@arcgis/core/views/SceneView';

import '@esri/calcite-components/dist/calcite/calcite.css';
import stylesDef from '../files/styles';
import styles from './annotationStyles';

const style = stylesDef(); // get your styles object

// Helper function to convert a style object to inline style string
function styleObjToString(styleObj: Record<string, string | number>): string {
  return Object.entries(styleObj)
    .map(([key, value]) => {
      const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      return `${kebabKey}:${value}`;
    })
    .join(';');
}

interface AnnotationProps {
  view: SceneView;
  clicked : boolean
}

class Annotation extends Widget {
  view: SceneView;

  isActive = false;

  showPanel = false;

  showExisting = false;

  isShowClicked = false;

  mapClicked: boolean;

  name = 'AnnotationEdit';

  constructor(params: AnnotationProps) {
    super(params);
    this.view = params.view;
    this.mapClicked = params.clicked;
    window.addEventListener('toggle-existing-annotations', () => {
      this._toggleExistingOrder();
    });
  }

  _toggleMainButton() {
    this.showPanel = !this.showPanel;
    this.scheduleRender();
    this.emit('open-annotations');
    if (!this.showPanel) {
      if (this.isShowClicked) {
        this._closePanel();
      }
    }
  }

  _startAnnotation() {
    this.isActive = true;
    this.emit('annotate-start', { active: true });
    if (this.isShowClicked === true) {
      this.showExisting = true;
    }
    this.scheduleRender();
  }

  _toggleExisting() {
    this.showExisting = !this.showExisting;
    if (this.showExisting) {
      this.emit('fetch-annotations');
      this.isShowClicked = true;
    } else {
      this.emit('hide-annotations');
    }
    this.scheduleRender();
  }

  _toggleExistingOrder() {
    this.showExisting = !!this.showExisting;
    if (this.showExisting) {
      this.emit('fetch-annotations');
    }
    this.scheduleRender();
  }

  _closePanel() {
    this.showPanel = false;
    this.scheduleRender();
  }

  // UI
  render() {
    return tsx(
      'div',
      { style: styleObjToString(styles.container) },

      tsx(
        'div',
        {
          class: 'esri-widget esri-widget--button',
          style: 'cursor:pointer',
          onclick: this._toggleMainButton.bind(this),
        },
        tsx('calcite-icon', {
          icon: this.showPanel ? 'x' : 'annotate-tool',
          scale: 'm',
          title: 'Annotation',
        }),
      ),

      /* EXPANDED PANEL */
      this.showPanel &&
      tsx(
        'div',
        { style: styleObjToString(styles.panel) },

        /* POINTY TRIANGLE */
        tsx('div', { style: styleObjToString(styles.triangle) }),

        /* NEW ANNOTATION BUTTON */
        this.mapClicked &&
        tsx(
          'button',
          { style: styleObjToString(styles.button), onclick: this._startAnnotation.bind(this) },
          [
            tsx('calcite-icon', {
              icon: 'pencil',
              scale: 's',
              style: 'margin-right:6px;',
              title: 'New Annotation',
            }),
            'New Annotation',
          ],
        ),

        /* SHOW/HIDE EXISTING ANNOTATIONS BUTTON */
        tsx(
          'button',
          { style: styleObjToString(styles.button), onclick: this._toggleExisting.bind(this) },
          [
            tsx('calcite-icon', {
              icon: this.showExisting ? 'view-hide' : 'view-visible',
              scale: 's',
              style: 'margin-right:6px;',
              title: this.showExisting ? 'Hide Annotation' : 'Show Annotation',
            }),
            this.showExisting ? 'Hide Annotation' : 'Show Annotation',
          ],
        ),
      ),
    );
  }
}

export default Annotation;
